import React, { useState } from 'react';
import { Calendar, X, Check, Gift, Clock, Euro, User, Mail, Phone } from 'lucide-react';
import AlertModal from './AlertModal';
import { useAlert } from '../hooks/UseAlert';

const PackageBooking = ({ packageInfo, onBack }) => {
  const [showBooking, setShowBooking] = useState(false);
  const [bookingData, setBookingData] = useState({
    name: '',
    email: '',
    phone: '',
    date: '',
    time: ''
  });
  const [loading, setLoading] = useState(false);

  const { alertConfig, warning, error, success, closeAlert } = useAlert();

  const allTimeSlots = ["08:00", "08:15", "08:30", "08:45", "09:00", "09:15", "09:30", "09:45", "10:00", "10:15", "10:30", "10:45", "11:00", "11:15", "11:30", "11:45", "12:00", "12:15", "12:30", "12:45", "13:00", "13:15", "13:30", "13:45", "14:00", "14:15", "14:30", "14:45", "15:00", "15:15", "15:30", "15:45", "16:00", "16:15", "16:30", "16:45", "17:00", "17:15", "17:30", "17:45", "18:00"];

  const packages = {
    36: {
      name: "Rituel des Trois Horizons",
      sessions: 3,
      discount: "10%",
      validity: "4 mois",
      description: "3 massages d'1h ou 1h30 au choix parmi toute notre gamme avec 10% de réduction sur chaque séance"
    },
    37: {
      name: "Voyage des Six Escales",
      sessions: 6,
      discount: "15%",
      validity: "7 mois",
      description: "6 massages d'1h ou 1h30 au choix parmi toute notre gamme avec 15% de réduction sur chaque séance"
    },
    38: {
      name: "L'Odyssée des Dix Pays",
      sessions: 10,
      discount: "20%",
      validity: "12 mois",
      description: "10 massages d'1h ou 1h30 au choix parmi toute notre gamme avec 20% de réduction sur chaque séance"
    }
  };

  const currentPackage = packages[packageInfo.id];

  const sendConfirmationEmail = async (bookingInfo) => {
    try {
      const response = await fetch('/api/send-email', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(bookingInfo)
      });

      const data = await response.json();
      return data.success;
    } catch (error) {
      console.error('Erreur email:', error);
      return false;
    }
  };

  const handleBooking = async () => {
    if (!bookingData.name || !bookingData.email || !bookingData.phone || !bookingData.date || !bookingData.time) {
      warning('Veuillez remplir tous les champs');
      return;
    }

    setLoading(true);

    try {
      const newPackage = {
        id: Date.now(),
        packageName: currentPackage.name,
        sessions: currentPackage.sessions,
        discount: currentPackage.discount,
        validity: currentPackage.validity,
        clientName: bookingData.name,
        clientEmail: bookingData.email,
        clientPhone: bookingData.phone,
        firstSessionDate: bookingData.date,
        firstSessionTime: bookingData.time,
        remainingSessions: currentPackage.sessions - 1,
        createdAt: new Date().toISOString(),
        status: 'active'
      };

      // Sauvegarder dans localStorage
      let existingPackages = [];
      const existingData = localStorage.getItem('all-packages');
      if (existingData) {
        existingPackages = JSON.parse(existingData);
      }
      existingPackages.push(newPackage);
      localStorage.setItem('all-packages', JSON.stringify(existingPackages));

      // Envoyer l'email de confirmation
      const emailSent = await sendConfirmationEmail({
        email: bookingData.email,
        name: bookingData.name,
        service: currentPackage.name,
        date: bookingData.date,
        time: bookingData.time,
        duration: "Forfait",
        price: `${currentPackage.discount} de réduction`
      });

      if (emailSent) {
        success(`Forfait confirmé !\n\n📧 Un email de confirmation a été envoyé à ${bookingData.email}\n\n${currentPackage.name}\n${currentPackage.sessions} séances avec ${currentPackage.discount} de réduction\n\nPremière séance :\n📅 ${new Date(bookingData.date).toLocaleDateString('fr-FR')}\n🕐 ${bookingData.time}\n\nMerci ${bookingData.name}, à bientôt ! ✨`);
      } else {
        warning(`Forfait confirmé !\n\n⚠️ L'email n'a pas pu être envoyé, mais votre forfait est bien enregistré.\n\nMerci ${bookingData.name} !`);
      }

      setShowBooking(false);
      setBookingData({ name: '', email: '', phone: '', date: '', time: '' });

    } catch (error) {
      console.error('Erreur:', error);
      error('Erreur lors de la réservation. Veuillez réessayer.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <AlertModal
        isOpen={alertConfig.isOpen}
        onClose={closeAlert}
        type={alertConfig.type}
        title={alertConfig.title}
        message={alertConfig.message}
        confirmText={alertConfig.confirmText}
        onConfirm={alertConfig.onConfirm}
        showCancel={alertConfig.showCancel}
        cancelText={alertConfig.cancelText}
      />

      <div className="max-w-6xl mx-auto">
        <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
          <div className="flex flex-col md:flex-row min-h-[600px]">
            {/* Section gauche - Icône décorative */}
            <div className="w-full md:w-2/5 bg-gradient-to-br from-purple-100 to-pink-100 flex items-center justify-center p-8">
              <div className="text-center">
                <div className="w-48 h-48 mx-auto mb-4 bg-white/50 rounded-full flex items-center justify-center">
                  <Gift className="w-24 h-24 text-purple-600" />
                </div>
                <div className="mt-6 bg-white/80 backdrop-blur-sm px-6 py-3 rounded-full">
                  <p className="text-3xl font-bold text-purple-600">{currentPackage.discount}</p>
                  <p className="text-sm text-purple-700">de réduction</p>
                </div>
              </div>
            </div>

            {/* Section droite - Détails */}
            <div className="w-full md:w-3/5 p-8 flex flex-col">
              <div className="flex-1">
                <h2 className="text-3xl font-light text-purple-900 mb-4">{currentPackage.name}</h2>

                {/* Infos principales */}
                <div className="flex flex-wrap gap-4 text-gray-600 mb-6 pb-6 border-b border-gray-200">
                  <div className="flex items-center gap-2">
                    <Gift className="w-5 h-5 text-purple-600" />
                    <span className="font-medium">{currentPackage.sessions} séances</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Clock className="w-5 h-5 text-purple-600" />
                    <span className="font-medium">Validité : {currentPackage.validity}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Euro className="w-5 h-5 text-purple-600" />
                    <span className="text-2xl font-semibold text-purple-700">{currentPackage.discount}</span>
                  </div>
                </div>

                <p className="text-gray-700 text-lg leading-relaxed mb-8">{currentPackage.description}</p>

                {/* Avantages du forfait */}
                <div className="bg-purple-50 rounded-lg p-6 mb-6">
                  <h3 className="text-lg font-semibold text-purple-900 mb-4 flex items-center gap-2">
                    <Check className="w-5 h-5 text-green-600" />
                    Votre forfait comprend :
                  </h3>
                  <div className="space-y-3">
                    <div className="flex items-start gap-3">
                      <Check className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                      <p className="text-gray-700">
                        <strong>{currentPackage.sessions} massages</strong> d'1h ou 1h30 au choix parmi toute notre gamme
                      </p>
                    </div>
                    <div className="flex items-start gap-3">
                      <Check className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                      <p className="text-gray-700">
                        <strong>{currentPackage.discount} de réduction</strong> sur chaque séance du forfait
                      </p>
                    </div>
                    <div className="flex items-start gap-3">
                      <Check className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                      <p className="text-gray-700">
                        <strong>Validité :</strong> {currentPackage.validity} à partir de la première séance
                      </p>
                    </div>
                    <div className="flex items-start gap-3">
                      <Check className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                      <p className="text-gray-700">
                        <strong>Flexibilité :</strong> Choisissez librement vos massages à chaque séance
                      </p>
                    </div>
                  </div>
                </div>

                {/* Info pratique */}
                <div className="bg-blue-50 border-l-4 border-blue-500 p-4 mb-4">
                  <p className="text-sm text-blue-800">
                    <strong>ℹ️ Comment ça marche ?</strong><br />
                    Réservez votre première séance ci-dessous. Une carte de fidélité vous sera remise lors de votre première visite.
                    Cette carte sera tamponnée à chaque séance et vous bénéficierez automatiquement de votre réduction.
                  </p>
                </div>
              </div>

              {/* Bouton de réservation */}
              {!showBooking ? (
                <button
                  onClick={() => setShowBooking(true)}
                  className="w-full bg-purple-600 text-white py-4 rounded-lg font-medium hover:bg-purple-700 transition-colors flex items-center justify-center gap-2"
                >
                  <Calendar className="w-5 h-5" />
                  Réserver ma première séance
                </button>
              ) : (
                <div className="bg-purple-50 p-6 rounded-lg">
                  <div className="flex justify-between items-center mb-4">
                    <h3 className="text-xl font-medium text-purple-900">Première séance</h3>
                    <button
                      onClick={() => setShowBooking(false)}
                      className="text-gray-500 hover:text-gray-700"
                    >
                      <X className="w-6 h-6" />
                    </button>
                  </div>

                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        <User className="w-4 h-4 inline mr-2" />
                        Nom complet
                      </label>
                      <input
                        type="text"
                        placeholder="Marie Dupont"
                        value={bookingData.name}
                        onChange={(e) => setBookingData({ ...bookingData, name: e.target.value })}
                        className="w-full px-4 py-3 rounded-lg border border-purple-200 focus:border-purple-500 focus:outline-none"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        <Mail className="w-4 h-4 inline mr-2" />
                        Email
                      </label>
                      <input
                        type="email"
                        placeholder="marie@exemple.com"
                        value={bookingData.email}
                        onChange={(e) => setBookingData({ ...bookingData, email: e.target.value })}
                        className="w-full px-4 py-3 rounded-lg border border-purple-200 focus:border-purple-500 focus:outline-none"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        <Phone className="w-4 h-4 inline mr-2" />
                        Téléphone
                      </label>
                      <input
                        type="tel"
                        placeholder="06 12 34 56 78"
                        value={bookingData.phone}
                        onChange={(e) => setBookingData({ ...bookingData, phone: e.target.value })}
                        className="w-full px-4 py-3 rounded-lg border border-purple-200 focus:border-purple-500 focus:outline-none"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        <Calendar className="w-4 h-4 inline mr-2" />
                        Date de la première séance
                      </label>
                      <input
                        type="date"
                        value={bookingData.date}
                        onChange={(e) => setBookingData({ ...bookingData, date: e.target.value })}
                        min={new Date().toISOString().split('T')[0]}
                        className="w-full px-4 py-3 rounded-lg border border-purple-200 focus:border-purple-500 focus:outline-none"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        <Clock className="w-4 h-4 inline mr-2" />
                        Horaire souhaité
                      </label>
                      <select
                        value={bookingData.time}
                        onChange={(e) => setBookingData({ ...bookingData, time: e.target.value })}
                        className="w-full px-4 py-3 rounded-lg border border-purple-200 focus:border-purple-500 focus:outline-none"
                        disabled={!bookingData.date}
                      >
                        <option value="">Choisir un créneau</option>
                        {allTimeSlots.map(time => (
                          <option key={time} value={time}>{time}</option>
                        ))}
                      </select>
                    </div>

                    <button
                      onClick={handleBooking}
                      disabled={loading}
                      className={`w-full bg-purple-600 text-white py-3 rounded-lg font-medium hover:bg-purple-700 transition-colors ${loading ? 'opacity-50 cursor-not-allowed' : ''
                        }`}
                    >
                      {loading ? 'Réservation en cours...' : 'Confirmer le forfait'}
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="mt-6 text-center">
          <button
            onClick={onBack}
            className="text-purple-600 hover:text-purple-800 underline"
          >
            Retour aux catégories
          </button>
        </div>
      </div>
    </>
  );
};

export default PackageBooking;